import { Slider, TextareaAutosize, Typography } from "@mui/material";
import React, { useEffect } from "react";
import { NEGATIVE_PROMPT } from "../../../../constants";

const PixverseEffectsSettings = ({
  onSettingsChange,
  selectedPrompt,
  generateExact,
}) => {
  const [settings, setSettings] = React.useState({
    effectType: "cinematic",
    presetEffect: "Hug",
    effectIntensity: 0.3,
    motionStrength: "moderate",
    aspectRatio: "1:1",
    resolution: "360p",
    customPrompt: selectedPrompt?.prompt || "",
    negativePrompt: NEGATIVE_PROMPT,
    seed: selectedPrompt?.seed && generateExact ? selectedPrompt?.seed : "",
  });

  React.useEffect(() => {
    // Call onSettingsChange when component mounts to set initial settings
    onSettingsChange?.(settings);
  }, []);

  const handleSettingChange = (key, value) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    onSettingsChange?.(newSettings);
    console.log("Pixverse Effects settings updated:", newSettings);
  };

  // Effects with their compatibility notes
  const effects = [
    { value: "Kiss Me AI", label: "Kiss Me AI", compatible: "All resolutions" },
    { value: "Kiss", label: "Kiss", compatible: "All resolutions" },
    {
      value: "Muscle Surge",
      label: "Muscle Surge",
      compatible: "All resolutions",
    },
    {
      value: "Warmth of Jesus",
      label: "Warmth of Jesus",
      compatible: "All resolutions",
    },
    {
      value: "Anything, Robot",
      label: "Anything, Robot",
      compatible: "All resolutions",
    },
    {
      value: "The Tiger Touch",
      label: "The Tiger Touch",
      compatible: "All resolutions",
    },
    { value: "Hug", label: "Hug", compatible: "All resolutions" },
    { value: "Holy Wings", label: "Holy Wings", compatible: "All resolutions" },
    { value: "Hulk", label: "Hulk", compatible: "1080p+ only" },
    { value: "Venom", label: "Venom", compatible: "1080p+ only" },
    { value: "Microwave", label: "Microwave", compatible: "All resolutions" },
  ];

  // Filter effects based on resolution
  const getCompatibleEffects = () => {
    if (settings.resolution === "720p") {
      return effects.filter(
        (effect) => effect.compatible === "All resolutions"
      );
    }
    return effects;
  };

  // Check if current effect is compatible with selected resolution
  const isCurrentEffectCompatible = () => {
    const compatibleEffects = getCompatibleEffects();
    return compatibleEffects.some(
      (effect) => effect.value === settings.presetEffect
    );
  };

  // Auto-switch to compatible effect if current one becomes incompatible
  useEffect(() => {
    if (!isCurrentEffectCompatible()) {
      handleSettingChange("presetEffect", "Hug"); // Default safe effect
    }
  }, [settings.resolution]);
  useEffect(() => {
    // Update settings when selectedPrompt changes
    if (selectedPrompt) {
      setSettings((prevSettings) => ({
        ...prevSettings,
        customPrompt: selectedPrompt?.prompt || "",
        negativePrompt: NEGATIVE_PROMPT,
        seed: selectedPrompt?.seed && generateExact ? selectedPrompt.seed : "",
      }));
    } else {
      setSettings((prevSettings) => ({
        ...prevSettings,
        customPrompt: "",
        negativePrompt: NEGATIVE_PROMPT,
        seed: "",
      }));
    }
  }, [selectedPrompt, generateExact]);

  return (
    <div className="space-y-4">
      <div>
        <Typography className="text-gray-300">Resolution</Typography>
        <select
          placeholder="Choose resolution"
          className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-white"
          value={settings.resolution}
          onChange={(e) => handleSettingChange("resolution", e.target.value)}
        >
          <option value="360p">360p</option>
          <option value="540p">540p</option>
          <option value="720p">720p</option>
          <option value="1080p">1080p (Full HD)</option>
        </select>
        <p className="text-xs text-gray-400 mt-1">
          Higher resolutions support more effects but take longer to process
        </p>
      </div>

      <div>
        <Typography className="text-gray-300">Preset Effects</Typography>
        <select
          value={settings.presetEffect}
          onChange={(e) => handleSettingChange("presetEffect", e.target.value)}
          placeholder="Choose a preset effect"
          className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-white"
        >
          {getCompatibleEffects().map((effect) => (
            <option key={effect.value} value={effect.value}>
              {effect.label}
              {effect.compatible !== "All resolutions" && (
                <span className="text-xs text-gray-400 ml-2">
                  ({effect.compatible})
                </span>
              )}
            </option>
          ))}
        </select>
        <p className="text-xs text-gray-400 mt-1">
          {settings.resolution === "720p"
            ? "Some effects are only available at 1080p+ resolution"
            : "All effects available at this resolution"}
        </p>
      </div>

      <div>
        <Typography className="text-gray-300">Effect Type</Typography>
        <select
          value={settings.effectType}
          onChange={(e) => handleSettingChange("effectType", e.target.value)}
          className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-white"
        >
          <option value="cinematic">Cinematic</option>
          <option value="artistic">Artistic</option>
          <option value="dramatic">Dramatic</option>
          <option value="vintage">Vintage</option>
          <option value="futuristic">Futuristic</option>
          <option value="noir">Film Noir</option>
          <option value="cyberpunk">Cyberpunk</option>
          <option value="fantasy">Fantasy</option>
        </select>
        <p className="text-xs text-gray-400 mt-1">
          Choose the type of visual effect to apply
        </p>
      </div>

      <div>
        <Typography className="text-gray-300">
          Effect Intensity: {settings.effectIntensity}
        </Typography>

        <Slider
          value={settings.effectIntensity}
          onChange={(e) =>
            handleSettingChange("effectIntensity", parseFloat(e.target.value))
          }
          aria-label="Default"
          valueLabelDisplay="auto"
          max={1.0}
          min={0.1}
          step={0.1}
          className="mt-2"
        />
        <p className="text-xs text-gray-400 mt-1">
          Controls how intense the visual effects are
        </p>
      </div>

      <div>
        <Typography className="text-gray-300">Motion Strength</Typography>
        <select
          value={settings.motionStrength}
          onChange={(e) =>
            handleSettingChange("motionStrength", e.target.value)
          }
          className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-white"
        >
          <option value="subtle">Subtle</option>
          <option value="moderate">Moderate</option>
          <option value="strong">Strong</option>
          <option value="extreme">Extreme</option>
        </select>
        <p className="text-xs text-gray-400 mt-1">
          How much motion to add to the video
        </p>
      </div>

      <div>
        <Typography className="text-gray-300">Aspect Ratio</Typography>
        <select
          value={settings.aspectRatio}
          onChange={(e) => handleSettingChange("aspectRatio", e.target.value)}
          className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-white"
        >
          <option value="16:9">16:9 (Widescreen)</option>
          <option value="9:16">9:16 (Portrait)</option>
          <option value="1:1">1:1 (Square)</option>
          <option value="4:3">4:3 (Classic)</option>
          <option value="original">Keep Original</option>
        </select>
        <p className="text-xs text-gray-400 mt-1">Output video aspect ratio</p>
      </div>

      <div>
        <Typography className="text-gray-300">Custom Prompt</Typography>
        <TextareaAutosize
          className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-white resize-none"
          rows={3}
          placeholder="Describe additional effects you want to apply..."
          value={settings.customPrompt}
          onChange={(e) => handleSettingChange("customPrompt", e.target.value)}
        />
        <p className="text-xs text-gray-400 mt-1">
          Optional: Describe specific effects you want
        </p>
      </div>
    </div>
  );
};

export default PixverseEffectsSettings;
